import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Checkbox, FormControlLabel, Modal } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import parse from 'html-react-parser';

import { confirmSharedBillingRiderDetails } from '../../../../api/services/Riders';
import { useResidenceAccountIdChanged } from '../useResidenceAccountIdChanged';

type CurrentRider = Pick<RiderFormType, 'isSharedBillingRider' | 'sharedBillingRider' | 'slug'>;
export const ConfirmationModal = ({
  needsConfirmation,
  onConfirm,
  onCancel,
  currentRider,
  riderStatus,
}: {
  needsConfirmation: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  currentRider: CurrentRider;
  riderStatus?: string;
}) => {
  const { watch } = useFormContext<RiderFormType>();
  const isSharedBillingRider = watch('isSharedBillingRider');
  const sharedBillingRider = watch('sharedBillingRider') as RiderFormType['sharedBillingRider'];
  const [open, setOpen] = useState(false);

  useResidenceAccountIdChanged(currentRider?.sharedBillingRider || null);

  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [hasConfirmed, setHasConfirmed] = useState(false);

  useEffect(() => {
    if (needsConfirmation) {
      getConfirmationMessage();
    }
  }, [needsConfirmation]);

  async function getConfirmationMessage() {
    const isAddingSBRDetails = !currentRider.isSharedBillingRider &&
      isSharedBillingRider && { ...sharedBillingRider, slug: currentRider?.slug };
    const isRemovingSBRDetails = riderStatus &&
      riderStatus == 'approved' &&
      currentRider.isSharedBillingRider &&
      !isSharedBillingRider &&
      currentRider?.slug && { slug: currentRider?.slug, inactive: true };
    const isUpdatingSBRDetails = currentRider.isSharedBillingRider &&
      isSharedBillingRider &&
      currentRider.sharedBillingRider?.residenceAccountId !== sharedBillingRider?.residenceAccountId && {
        ...sharedBillingRider,
        slug: currentRider?.slug,
      };

    const params = isAddingSBRDetails || isRemovingSBRDetails || isUpdatingSBRDetails;

    if (params) {
      const abortController = new AbortController();
      const response = await confirmSharedBillingRiderDetails(abortController.signal, params);

      if (response.type === 'success' && !abortController.signal.aborted) {
        setConfirmationMessage(response.data.message);
        setOpen(true);
      }
    } else {
      onConfirm();
    }
  }

  return (
    <Modal open={open}>
      <Modal.Title>Shared Rider update confirmation</Modal.Title>
      <Modal.Content sx={{ whiteSpace: 'pre-wrap' }}>
        {parse(confirmationMessage)}
        {isSharedBillingRider && (
          <FormControlLabel
            sx={{ mt: '1.5rem' }}
            control={
              <Checkbox
                sx={{ ml: '.5rem' }}
                id="confirmed"
                data-testid="confirmed"
                onChange={(e) => setHasConfirmed(e.target.checked)}
              />
            }
            label="I agree to the change in shared status"
          />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button
          color="tertiary"
          onClick={() => {
            onCancel();
            setOpen(false);
          }}
        >
          Back
        </Button>
        <Button
          color="primary"
          disabled={isSharedBillingRider ? !hasConfirmed : false}
          onClick={() => {
            onConfirm();
            setOpen(false);
          }}
        >
          Confirm
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
